import { MAX_SCORE_PRECISION_DIGITS, backpackFragmentCount, fragmentConstraintIgnored, itemFragmentCount, resourceGroupForCharacter, totalFragmentCount, userPlanFromWire, } from '@minglun/domain';
const RECORD_START = '记录开始';
const RECORD_END = '记录结束';
export const SOLVER_RECORD_FILE_VERSION = 4;
const SECTION_CONFIGURATION = '求解器配置';
const SECTION_OBJECTIVES = '求解方案：优化目标';
const SECTION_BASE = '求解方案：参考属性';
const SECTION_INVENTORY = '求解方案：资源';
const SECTION_SSR_CHOICES = '求解结果：SSR自选建议';
const SECTION_WHEELS = '求解结果：关系命轮';
const SECTION_ATTRIBUTES = '求解结果：属性加成';
const SECTION_COMPARISON_EXECUTION = '结果对比：执行进度';
const SECTION_FRAGMENT_SHORTAGES = '碎片分析：碎片缺口';
const SECTION_FRAGMENT_CHANGES = '碎片分析：理想方案对比';
const RESOURCE_LABELS = { UR: 'UR', SSR_LIMITED: '限定 SSR', SSR: '普通 SSR', SR: 'SR', R: 'R' };
const STATUS_LABELS = { not_run: '未计算', running: '计算中', completed: '已完成', optimal: '全局最优', feasible: '可行解', time_limit: '限时可行', cancelled: '已取消', failed: '失败' };
const STATUS_BY_LABEL = new Map(Object.entries(STATUS_LABELS).map(([status, label]) => [label, status]));
export function serializeSolverRecords(workbench, entries) {
    const lines = ['命轮工坊求解记录', `协议版本：${SOLVER_RECORD_FILE_VERSION}`, `记录数量：${entries.length}`, ''];
    entries.forEach((entry, index) => { if (index)
        lines.push(''); lines.push(...serializeEntry(workbench, entry)); });
    return `${lines.join('\n').trimEnd()}\n`;
}
export function parseSolverRecords(text, workbench) {
    return parseSolverRecordsInternal(text, workbench, false);
}
function parseSolverRecordsInternal(text, workbench, tolerant) {
    const trimmed = text.trim();
    if (!trimmed)
        return { records: [] };
    const lines = trimmed.split(/\r?\n/);
    if (!isCurrentSolverRecordFile(text)) {
        throw new Error(`求解记录不是当前协议版本 ${SOLVER_RECORD_FILE_VERSION}。`);
    }
    const records = [];
    const migrationWarnings = [];
    let start = lines.indexOf(RECORD_START);
    while (start >= 0) {
        const end = lines.indexOf(RECORD_END, start + 1);
        if (end < 0)
            throw new Error('求解记录缺少“记录结束”。');
        records.push(parseEntry(lines.slice(start + 1, end), workbench, tolerant ? migrationWarnings : undefined));
        start = lines.indexOf(RECORD_START, end + 1);
    }
    if (!records.length && lines.some((line) => line.trim() === '记录数量：0'))
        return { records: [] };
    if (!records.length)
        throw new Error('文件中没有可识别的求解记录。');
    return { records, lastResult: records[0]?.result, ...(migrationWarnings.length ? { migrationWarnings: [...new Set(migrationWarnings)] } : {}) };
}
export function isCurrentSolverRecordFile(text) {
    const lines = text.trimStart().split(/\r?\n/, 2);
    return lines[0] === '命轮工坊求解记录'
        && lines[1] === `协议版本：${SOLVER_RECORD_FILE_VERSION}`;
}
/**
 * One-time import path for record files written by earlier releases. The
 * normal parser remains deliberately strict; callers must rewrite the parsed
 * records with serializeSolverRecords before treating them as current data.
 */
export function parseMigratableSolverRecords(text, workbench) {
    if (isCurrentSolverRecordFile(text))
        return parseSolverRecordsInternal(text, workbench, true);
    const trimmed = text.trim();
    if (!trimmed)
        return { records: [] };
    if (trimmed.startsWith('{'))
        return parseLegacyJsonRecords(trimmed);
    if (trimmed.startsWith('命轮工坊求解记录')) {
        return parseSolverRecordsInternal(normalizeLegacyTextRecordFile(trimmed), workbench, true);
    }
    throw new Error('旧求解记录格式无法识别，原文件已保留。');
}
function parseLegacyJsonRecords(text) {
    let raw;
    try {
        raw = JSON.parse(text);
    }
    catch (error) {
        throw new Error(`旧求解记录 JSON 无法解析：${error instanceof Error ? error.message : String(error)}`);
    }
    if (raw.format !== 'minglun-result-history' || !Array.isArray(raw.records)) {
        throw new Error('旧求解记录 JSON 缺少可识别的记录集合。');
    }
    const first = raw.records[0];
    const parsedRecords = first && ('Configuration' in first || 'Result' in first)
        ? userPlanFromWire({
            SchemaVersion: '4.1',
            Id: 'migration',
            Name: '迁移',
            WorkbenchVersion: '',
            BaseAttributes: [],
            Objectives: [],
            Inventory: [],
            Solver: {},
            SolverHistory: raw.records,
        }).solverHistory
        : raw.records.map(normalizeLegacyJsonEntry);
    const records = parsedRecords.map((entry) => ({
        ...entry,
        result: {
            ...entry.result,
            stageModelVersion: entry.result.stageModelVersion ?? 'legacy-incompatible',
        },
    }));
    return {
        records,
        ...(records[0]?.result ? { lastResult: records[0].result } : {}),
    };
}
function normalizeLegacyJsonEntry(raw) {
    const entry = asRecord(raw);
    const configuration = asRecord(entry.configuration);
    const result = asRecord(entry.result);
    if (!Array.isArray(entry.objectives) || !Array.isArray(entry.baseAttributes)
        || !Array.isArray(entry.inventory) || !Array.isArray(result.fateWheels)
        || !Array.isArray(result.finalAttributes)) {
        throw new Error('旧求解记录 JSON 的条目结构不完整。');
    }
    const parameters = Array.isArray(configuration.parameters)
        ? configuration.parameters.map((item) => {
            const value = asRecord(item);
            return { key: String(value.key ?? ''), value: String(value.value ?? '') };
        }).filter((item) => item.key)
        : [];
    const normalizedConfiguration = {
        algorithmId: String(configuration.algorithmId ?? 'solver.highs-mip'),
        includeDestinyWheel: configuration.includeDestinyWheel !== false,
        ignoredFragmentGroups: Array.isArray(configuration.ignoredFragmentGroups)
            ? configuration.ignoredFragmentGroups
            : [],
        ssrWheelChoices: finiteNumber(configuration.ssrWheelChoices),
        ssrChoiceBalanceEnabled: configuration.ssrChoiceBalanceEnabled === true,
        preferFewerWheels: configuration.preferFewerWheels !== false,
        fragmentAnalysisEnabled: configuration.fragmentAnalysisEnabled === true,
        parameters,
    };
    const normalizedResult = {
        status: String(result.status ?? 'completed'),
        algorithmId: String(result.algorithmId ?? normalizedConfiguration.algorithmId),
        startedAt: String(result.startedAt ?? entry.createdAt ?? ''),
        completedAt: String(result.completedAt ?? entry.createdAt ?? ''),
        elapsedMilliseconds: finiteNumber(result.elapsedMilliseconds),
        ...(typeof result.objectiveScore === 'number' ? { objectiveScore: result.objectiveScore } : {}),
        isProvenOptimal: result.isProvenOptimal === true,
        fateWheels: result.fateWheels,
        finalAttributes: result.finalAttributes,
        ...(Array.isArray(result.ssrChoiceSuggestions) ? { ssrChoiceSuggestions: result.ssrChoiceSuggestions } : {}),
        ...(typeof result.ssrChoiceProtectionTarget === 'number' ? { ssrChoiceProtectionTarget: result.ssrChoiceProtectionTarget } : {}),
        ...(typeof result.ssrChoiceProtectionAchieved === 'number' ? { ssrChoiceProtectionAchieved: result.ssrChoiceProtectionAchieved } : {}),
        ...(result.fragmentAnalysis && typeof result.fragmentAnalysis === 'object' ? { fragmentAnalysis: result.fragmentAnalysis } : {}),
        stageModelVersion: typeof result.stageModelVersion === 'string' ? result.stageModelVersion : 'legacy-incompatible',
        ...(typeof result.message === 'string' ? { message: result.message } : {}),
    };
    return {
        id: String(entry.id ?? `record.${stableFingerprint(JSON.stringify(raw))}`),
        title: String(entry.title ?? '迁移的求解记录'),
        createdAt: String(entry.createdAt ?? normalizedResult.completedAt),
        workbenchVersion: String(entry.workbenchVersion ?? ''),
        ...(typeof entry.studyId === 'string' ? { studyId: entry.studyId } : {}),
        ...(typeof entry.studyName === 'string' ? { studyName: entry.studyName } : {}),
        ...(typeof entry.weightFocusExponent === 'number' ? { weightFocusExponent: entry.weightFocusExponent } : {}),
        ...(typeof entry.scorePrecisionDigits === 'number' ? { scorePrecisionDigits: entry.scorePrecisionDigits } : {}),
        ...(Array.isArray(entry.comparisonExecutions) ? { comparisonExecutions: entry.comparisonExecutions } : {}),
        configuration: normalizedConfiguration,
        objectives: entry.objectives,
        baseAttributes: entry.baseAttributes,
        inventory: entry.inventory,
        result: normalizedResult,
    };
}
function normalizeLegacyTextRecordFile(text) {
    const source = text.replace(/\r\n/g, '\n');
    const lines = source.split('\n');
    if (lines[1]?.startsWith('协议版本：'))
        lines[1] = `协议版本：${SOLVER_RECORD_FILE_VERSION}`;
    else
        lines.splice(1, 0, `协议版本：${SOLVER_RECORD_FILE_VERSION}`);
    return lines.join('\n')
        .replaceAll('碎片分析：缺少碎片', SECTION_FRAGMENT_SHORTAGES)
        .replaceAll('碎片分析：总缺口', SECTION_FRAGMENT_SHORTAGES)
        .replaceAll('碎片分析：方案变化', SECTION_FRAGMENT_CHANGES)
        .replaceAll('碎片分析：理想方案\n', `${SECTION_FRAGMENT_CHANGES}\n`)
        .replaceAll('碎片方案分析：', '碎片分析：')
        .replaceAll('总盘数量惩罚：', '关系精简：')
        .replaceAll('同分精简：', '关系精简：')
        .replaceAll('目标值：', '评分：')
        .replaceAll('放宽后目标值：', '理想方案评分：')
        .replaceAll('综合提升：', '评分提升：')
        .replaceAll('｜约束 ', '｜已有 ');
}
function asRecord(value) {
    return value && typeof value === 'object' ? value : {};
}
function finiteNumber(value) {
    return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}
function serializeEntry(workbench, entry) {
    const algorithm = workbench.defaults.solverAlgorithms.find((item) => item.id === entry.configuration.algorithmId);
    const parameterByKey = new Map(algorithm?.parameters.map((item) => [item.key, item.name]) ?? []);
    const metricById = new Map(workbench.optimizationMetrics.map((item) => [item.id, item]));
    const characterById = new Map(workbench.characters.map((item) => [item.id, item]));
    const categoryById = new Map(workbench.wheelCategories.map((item) => [item.id, item]));
    const wheelById = new Map(workbench.fateWheels.map((item) => [item.id, item]));
    const sourceById = new Map(workbench.sourceAttributes.map((item) => [item.id, item]));
    const ignored = entry.inventory.filter((item) => fragmentConstraintIgnored(item, characterById.get(item.characterId), entry.configuration.ignoredFragmentGroups)).length;
    const analysis = entry.result.fragmentAnalysis;
    const lines = [
        RECORD_START,
        `标题：${singleLine(entry.title)}`,
        ...(entry.studyName ? [`研究：${singleLine(entry.studyName)}`] : []),
        ...(entry.studyId ? [`研究标识：${singleLine(entry.studyId)}`] : []),
        `时间：${friendlyTime(entry.createdAt)}`,
        `状态：${STATUS_LABELS[entry.result.status]}`,
        `模型版本：${entry.result.stageModelVersion ?? 'legacy-incompatible'}`,
        `评分：${entry.result.objectiveScore === undefined ? '无' : formatNumber(entry.result.objectiveScore)}`,
        `耗时：${formatNumber(entry.result.elapsedMilliseconds / 1000)} 秒`,
        ...(entry.result.ssrChoiceProtectionTarget === undefined ? [] : [`随机保护目标：${formatNumber(entry.result.ssrChoiceProtectionTarget * 100)}%`]),
        ...(entry.result.ssrChoiceProtectionAchieved === undefined ? [] : [`随机保护结果：${formatNumber(entry.result.ssrChoiceProtectionAchieved * 100)}%`]),
        ...(entry.result.message ? [`方案整理：${singleLine(entry.result.message)}`] : []),
        ...(analysis ? [
            `碎片分析状态：${analysis.status === 'completed' ? '精确完成' : analysis.status === 'estimated' ? '估算结果' : analysis.status === 'cancelled' ? '已取消' : '失败'}`,
            `理想方案评分：${analysis.relaxedObjectiveScore === undefined ? '无' : formatNumber(analysis.relaxedObjectiveScore)}`,
            `评分提升：${analysis.objectiveImprovement === undefined ? '无' : formatNumber(analysis.objectiveImprovement)}`,
            `碎片分析耗时：${formatNumber(analysis.elapsedMilliseconds / 1000)} 秒`,
            ...(analysis.message ? [`碎片分析说明：${singleLine(analysis.message)}`] : [])
        ] : []),
        '', SECTION_CONFIGURATION,
        `求解器：${algorithm?.name ?? 'HiGHS 整数规划'}`,
        `宿命之轮：${entry.configuration.includeDestinyWheel ? '启用' : '停用'}`,
        `忽略碎片：${ignored ? `${ignored} 个命轮` : '无'}`,
        `SSR 命轮自选：${entry.configuration.ssrWheelChoices}`,
        `SSR自选均衡：${entry.configuration.ssrChoiceBalanceEnabled ? '启用' : '停用'}`,
        `权重聚焦度：${formatNumber(entry.weightFocusExponent ?? workbench.globalSettings.weightFocusExponent)}`,
        `内部评分精度：1e-${entry.scorePrecisionDigits ?? workbench.globalSettings.scorePrecisionDigits}`,
        `关系精简：${entry.configuration.preferFewerWheels ? '启用' : '停用'}`,
        `碎片分析：${entry.configuration.fragmentAnalysisEnabled ? '启用' : '停用'}`
    ];
    for (const parameter of entry.configuration.parameters.filter((item) => item.key !== 'time_limit_seconds' && item.key !== 'threads'))
        lines.push(`参数 ${parameterByKey.get(parameter.key) ?? parameter.key}：${singleLine(parameter.value)}`);
    lines.push('', SECTION_OBJECTIVES);
    for (const objective of entry.objectives)
        lines.push(`${metricById.get(objective.metricId)?.name ?? objective.metricId}｜权重 ${formatNumber(objective.weight)}｜${objective.isEnabled ? '启用' : '停用'}`);
    lines.push('', SECTION_BASE);
    for (const item of entry.baseAttributes)
        lines.push(`${metricById.get(item.metricId)?.name ?? item.metricId}｜${formatNumber(item.value)}`);
    lines.push('', SECTION_INVENTORY);
    for (const item of entry.inventory.filter((value) => value.wheelCount || totalFragmentCount(value) || fragmentConstraintIgnored(value, characterById.get(value.characterId), entry.configuration.ignoredFragmentGroups)))
        lines.push(`${characterById.get(item.characterId)?.name ?? item.characterId}｜命轮 ${item.wheelCount}｜背包 ${backpackFragmentCount(item)}｜道具 ${itemFragmentCount(item)}｜${fragmentConstraintIgnored(item, characterById.get(item.characterId), entry.configuration.ignoredFragmentGroups) ? '忽略碎片' : '约束碎片'}`);
    if (!entry.inventory.some((value) => value.wheelCount || totalFragmentCount(value) || fragmentConstraintIgnored(value, characterById.get(value.characterId), entry.configuration.ignoredFragmentGroups)))
        lines.push('无');
    if (entry.configuration.ssrWheelChoices > 0) {
        lines.push('', SECTION_SSR_CHOICES);
        for (const item of entry.result.ssrChoiceSuggestions ?? [])
            lines.push(`${characterById.get(item.characterId)?.name ?? item.characterId}｜${item.count}`);
        if (!entry.result.ssrChoiceSuggestions?.length)
            lines.push('无');
    }
    lines.push('', SECTION_WHEELS);
    for (const result of entry.result.fateWheels.filter((item) => item.rank || item.star)) {
        const wheel = wheelById.get(result.fateWheelId);
        const category = wheel ? categoryById.get(wheel.wheelCategoryId) : undefined;
        const members = wheel?.members.map((member) => characterById.get(member.characterId)?.name ?? member.characterId).join('、') ?? '';
        lines.push(`${wheel?.name ?? result.fateWheelId}｜${category?.name ?? '未知类别'}｜${members || '无伙伴'}｜${result.rank} 阶｜${result.star} 星｜${result.isApplied ? '已执行' : '待执行'}`);
    }
    if (!entry.result.fateWheels.some((item) => item.rank || item.star))
        lines.push('无');
    lines.push('', SECTION_ATTRIBUTES);
    for (const item of entry.result.finalAttributes)
        lines.push(`${sourceById.get(item.sourceAttributeId)?.name ?? item.sourceAttributeId}｜${item.valueKind === 'percent' ? '百分比' : '数值'}｜${formatNumber(item.value)}`);
    if (!entry.result.finalAttributes.length)
        lines.push('无');
    const comparisonExecutions = (entry.comparisonExecutions ?? []).filter((item) => item.appliedFateWheelIds.length);
    if (comparisonExecutions.length) {
        lines.push('', SECTION_COMPARISON_EXECUTION);
        for (const item of comparisonExecutions) {
            const applied = item.appliedFateWheelIds.map((id) => wheelById.get(id)?.name ?? id).join('、');
            lines.push(`基准 ${friendlyTime(item.baselineCompletedAt)}｜已执行 ${applied}`);
        }
    }
    if (analysis) {
        lines.push('', SECTION_FRAGMENT_SHORTAGES);
        for (const item of analysis.shortages) {
            const affected = item.affectedFateWheelIds.map((id) => wheelById.get(id)?.name ?? id).join('、') || '无';
            const contributionPerItemBasisPoints = item.shortage > 0 ? item.contributionPercent / item.shortage * 100 : 0;
            lines.push(`${characterById.get(item.characterId)?.name ?? item.characterId}｜已有 ${item.available}｜需求 ${item.required}｜缺少 ${item.shortage}｜每片贡献 ${formatNumber(contributionPerItemBasisPoints)}‱/个｜影响 ${affected}`);
        }
        if (!analysis.shortages.length)
            lines.push('无');
        lines.push('', SECTION_FRAGMENT_CHANGES);
        for (const item of analysis.wheelChanges) {
            const wheel = wheelById.get(item.fateWheelId);
            const category = wheel ? categoryById.get(wheel.wheelCategoryId) : undefined;
            const members = wheel?.members.map((member) => characterById.get(member.characterId)?.name ?? member.characterId).join('、') ?? '';
            const gains = item.gains.map((gain) => `${sourceById.get(gain.sourceAttributeId)?.name ?? gain.sourceAttributeId} ${gain.value > 0 ? '+' : ''}${formatNumber(gain.value)}${gain.valueKind === 'percent' ? '%' : ''}`).join('、') || '无变化';
            lines.push(`${wheel?.name ?? item.fateWheelId}｜${category?.name ?? '未知类别'}｜${members || '无伙伴'}｜${item.fromRank} 阶 ${item.fromStar} 星 → ${item.toRank} 阶 ${item.toStar} 星｜贡献 ${formatNumber(item.contributionPercent)}%｜收益 ${gains}`);
        }
        if (!analysis.wheelChanges.length)
            lines.push('无');
    }
    lines.push(RECORD_END);
    return lines;
}
function parseEntry(lines, workbench, migrationWarnings) {
    const values = new Map();
    const sections = new Map();
    let section = '';
    for (const raw of lines) {
        const line = raw.trim();
        if (!line)
            continue;
        if ([SECTION_CONFIGURATION, SECTION_OBJECTIVES, SECTION_BASE, SECTION_INVENTORY, SECTION_SSR_CHOICES, SECTION_WHEELS, SECTION_ATTRIBUTES, SECTION_COMPARISON_EXECUTION, SECTION_FRAGMENT_SHORTAGES, SECTION_FRAGMENT_CHANGES].includes(line)) {
            section = line;
            sections.set(section, []);
            continue;
        }
        if (section)
            sections.get(section)?.push(line);
        else {
            const split = line.indexOf('：');
            if (split > 0)
                values.set(line.slice(0, split), line.slice(split + 1));
        }
    }
    const title = values.get('标题') ?? '导入的求解记录';
    const studyName = values.get('研究');
    const studyId = values.get('研究标识');
    const createdAt = parseFriendlyTime(values.get('时间'));
    const status = STATUS_BY_LABEL.get(values.get('状态') ?? '') ?? 'completed';
    const algorithmLine = findValue(sections.get(SECTION_CONFIGURATION), '求解器') ?? '';
    const algorithm = workbench.defaults.solverAlgorithms.find((item) => item.name === algorithmLine) ?? workbench.defaults.solverAlgorithms.find((item) => item.isEnabled) ?? workbench.defaults.solverAlgorithms[0];
    if (!algorithm)
        throw new Error('当前工作台没有可用求解器，无法导入记录。');
    const parameterByName = new Map(algorithm.parameters.map((item) => [item.name, item]));
    const configurationLines = sections.get(SECTION_CONFIGURATION) ?? [];
    const ignoredText = findValue(configurationLines, '忽略碎片') ?? '无';
    const ignoredFragmentGroups = Object.entries(RESOURCE_LABELS).filter(([, label]) => ignoredText.split('、').includes(label)).map(([group]) => group);
    const parameters = configurationLines.filter((line) => line.startsWith('参数 ')).map((line) => { const split = line.indexOf('：'); const name = line.slice(3, split); return { key: parameterByName.get(name)?.key ?? name, value: line.slice(split + 1) }; });
    const metricByName = new Map(workbench.optimizationMetrics.map((item) => [item.name, item]));
    const objectives = (sections.get(SECTION_OBJECTIVES) ?? []).filter((line) => line !== '无').flatMap((line) => { const [name = '', weightPart = '', enabledPart = '启用'] = line.split('｜'); const metric = metricByName.get(name); if (!metric) {
        if (!migrationWarnings)
            throw new Error(`当前工作台没有目标“${name}”。`);
        migrationWarnings.push(`旧结果中的目标“${name}”已不存在，确认后将删除。`);
        return [];
    } return [{ metricId: metric.id, weight: Math.round(Math.max(0, Math.min(100, parseNumber(weightPart.replace('权重 ', ''), `目标“${name}”权重`)))), isEnabled: enabledPart === '启用' }]; });
    const baseAttributes = (sections.get(SECTION_BASE) ?? []).filter((line) => line !== '无').flatMap((line) => { const [name = '', number = ''] = line.split('｜'); const metric = metricByName.get(name); if (!metric) {
        if (!migrationWarnings)
            throw new Error(`当前工作台没有参考属性“${name}”。`);
        migrationWarnings.push(`旧结果中的参考属性“${name}”已不存在，确认后将删除。`);
        return [];
    } return [{ metricId: metric.id, value: parseNumber(number, `参考属性“${name}”`) }]; });
    const characterByName = new Map(workbench.characters.map((item) => [item.name, item]));
    const resourceLines = new Map((sections.get(SECTION_INVENTORY) ?? []).filter((line) => line !== '无').map((line) => {
        const [name = '', ...parts] = line.split('｜');
        const value = (prefix) => parts.find((part) => part.startsWith(`${prefix} `))?.slice(prefix.length + 1);
        return [name, {
                wheelCount: parseNumber(value('命轮') ?? '0', `命轮“${name}”数量`),
                backpackFragmentCount: parseNumber(value('背包') ?? value('碎片') ?? '0', `命轮“${name}”背包碎片`),
                itemFragmentCount: parseNumber(value('道具') ?? '0', `命轮“${name}”道具碎片`),
                ignoreFragments: parts.includes('忽略碎片'),
            }];
    }));
    for (const name of resourceLines.keys())
        if (!characterByName.has(name)) {
            if (!migrationWarnings)
                throw new Error(`当前工作台没有命轮“${name}”。`);
            migrationWarnings.push(`旧结果中的命轮“${name}”已不存在，其资源将删除。`);
        }
    const inventory = workbench.characters.map((character) => {
        const resource = resourceLines.get(character.name) ?? { wheelCount: 0, backpackFragmentCount: 0, itemFragmentCount: 0, ignoreFragments: false };
        return { characterId: character.id, ...resource, ignoreFragments: resource.ignoreFragments || ignoredFragmentGroups.includes(resourceGroupForCharacter(character)) };
    });
    const categoryById = new Map(workbench.wheelCategories.map((item) => [item.id, item]));
    const wheelKeys = new Map(workbench.fateWheels.map((wheel) => { const members = wheel.members.map((member) => workbench.characters.find((item) => item.id === member.characterId)?.name ?? member.characterId).join('、'); return [[wheel.name, categoryById.get(wheel.wheelCategoryId)?.name ?? '', members].join('｜'), wheel]; }));
    const fateWheels = (sections.get(SECTION_WHEELS) ?? []).filter((line) => line !== '无').flatMap((line) => { const [name = '', category = '', members = '', rank = '', star = '', progress = '待执行'] = line.split('｜'); const wheel = wheelKeys.get([name, category, members].join('｜')); if (!wheel) {
        if (!migrationWarnings)
            throw new Error(`当前工作台无法匹配关系命轮“${name}”（${category}／${members}）。`);
        migrationWarnings.push(`旧结果中的关系命轮“${name}”（${category}／${members}）无法匹配，确认后将删除。`);
        return [];
    } return [{ fateWheelId: wheel.id, rank: parseNumber(rank.replace(' 阶', ''), `关系命轮“${name}”阶数`), star: parseNumber(star.replace(' 星', ''), `关系命轮“${name}”星数`), isApplied: progress === '已执行' }]; });
    const sourceByName = new Map(workbench.sourceAttributes.map((item) => [item.name, item]));
    const finalAttributes = (sections.get(SECTION_ATTRIBUTES) ?? []).filter((line) => line !== '无').flatMap((line) => { const [name = '', kind = '', number = ''] = line.split('｜'); const source = sourceByName.get(name); if (!source) {
        if (!migrationWarnings)
            throw new Error(`当前工作台没有结果属性“${name}”。`);
        migrationWarnings.push(`旧结果中的属性“${name}”已不存在，确认后将重新汇总属性。`);
        return [];
    } return [{ sourceAttributeId: source.id, valueKind: kind === '百分比' ? 'percent' : 'flat', value: parseNumber(number, `结果属性“${name}”`) }]; });
    const wheelByName = new Map(workbench.fateWheels.map((wheel) => [wheel.name, wheel]));
    const comparisonExecutions = (sections.get(SECTION_COMPARISON_EXECUTION) ?? []).filter((line) => line !== '无').map((line) => {
        const [baselineText = '', appliedText = ''] = line.split('｜');
        const baselineCompletedAt = parseFriendlyTime(baselineText.replace(/^基准 /, ''));
        const names = appliedText.replace(/^已执行 /, '').split('、').filter(Boolean);
        const appliedFateWheelIds = names.flatMap((name) => {
            const wheel = wheelByName.get(name);
            if (wheel)
                return [wheel.id];
            if (!migrationWarnings)
                throw new Error(`当前工作台没有关系命轮“${name}”，无法读取结果对比执行进度。`);
            migrationWarnings.push(`旧结果执行进度中的关系命轮“${name}”已不存在，确认后将删除该项。`);
            return [];
        });
        return { baselineCompletedAt, appliedFateWheelIds };
    }).filter((item) => item.appliedFateWheelIds.length);
    const ssrChoiceLines = (sections.get(SECTION_SSR_CHOICES) ?? []).filter((line) => line !== '无');
    const ssrChoiceSuggestions = ssrChoiceLines.filter((line) => !line.startsWith('暂留｜')).flatMap((line) => {
        const [name = '', count = ''] = line.split('｜');
        const character = characterByName.get(name);
        if (!character || character.rarity !== 'SSR') {
            if (!migrationWarnings)
                throw new Error(`当前工作台没有 SSR 命轮“${name}”。`);
            migrationWarnings.push(`旧结果的 SSR 自选建议“${name}”已不存在，确认后将删除。`);
            return [];
        }
        return [{ characterId: character.id, count: Math.max(0, Math.floor(parseNumber(count, `SSR自选“${name}”数量`))) }];
    }).filter((item) => item.count > 0);
    const elapsedMilliseconds = Math.round(parseNumber((values.get('耗时') ?? '0').replace(' 秒', ''), '求解耗时') * 1000);
    const objectiveText = values.get('评分');
    const objectiveScore = objectiveText && objectiveText !== '无' ? parseNumber(objectiveText, '评分') : undefined;
    const analysisStatusLabel = values.get('碎片分析状态');
    let fragmentAnalysis;
    if (analysisStatusLabel) {
        try {
            fragmentAnalysis = parseFragmentAnalysis(values, sections, wheelKeys, characterByName, sourceByName, objectiveScore);
        }
        catch (error) {
            if (!migrationWarnings)
                throw error;
            migrationWarnings.push(`旧结果的碎片分析无法完整映射，确认后将删除；重新计算即可生成新版分析。`);
        }
    }
    const resultMessage = values.get('方案整理');
    const focusText = findValue(configurationLines, '权重聚焦度');
    const precisionText = findValue(configurationLines, '内部评分精度');
    const protectionTargetText = values.get('随机保护目标');
    const protectionAchievedText = values.get('随机保护结果');
    const result = { status, algorithmId: algorithm.id, startedAt: createdAt, completedAt: createdAt, elapsedMilliseconds, ...(objectiveScore === undefined ? {} : { objectiveScore }), isProvenOptimal: status === 'optimal', fateWheels, finalAttributes, ...(ssrChoiceSuggestions.length ? { ssrChoiceSuggestions } : {}), ...(protectionTargetText ? { ssrChoiceProtectionTarget: parseNumber(protectionTargetText.replace('%', ''), '随机保护目标') / 100 } : {}), ...(protectionAchievedText ? { ssrChoiceProtectionAchieved: parseNumber(protectionAchievedText.replace('%', ''), '随机保护结果') / 100 } : {}), ...(fragmentAnalysis ? { fragmentAnalysis } : {}), stageModelVersion: values.get('模型版本') ?? 'legacy-incompatible', ...(resultMessage ? { message: resultMessage } : {}) };
    const fingerprint = stableFingerprint(lines.join('\n'));
    const compactText = findValue(configurationLines, '关系精简');
    return { id: `record.${fingerprint}`, title, createdAt, workbenchVersion: workbench.version, ...(studyId ? { studyId } : {}), ...(studyName ? { studyName } : {}), ...(focusText ? { weightFocusExponent: Math.max(1, Math.min(10, parseNumber(focusText, '权重聚焦度'))) } : {}), ...(precisionText ? { scorePrecisionDigits: parseScorePrecisionDigits(precisionText) } : {}), ...(comparisonExecutions.length ? { comparisonExecutions } : {}), configuration: { algorithmId: algorithm.id, includeDestinyWheel: findValue(configurationLines, '宿命之轮') === '启用', ignoredFragmentGroups: [], ssrWheelChoices: parseNumber(findValue(configurationLines, 'SSR 命轮自选') ?? '0', 'SSR 命轮自选'), ssrChoiceBalanceEnabled: findValue(configurationLines, 'SSR自选均衡') === '启用', preferFewerWheels: compactText === '启用', fragmentAnalysisEnabled: findValue(configurationLines, '碎片分析') === '启用', parameters: parameters.filter((item) => item.key !== 'time_limit_seconds' && item.key !== 'threads') }, objectives, baseAttributes, inventory, result };
}
function parseScorePrecisionDigits(value) {
    const normalized = value.trim();
    const scientific = normalized.match(/^1e-(\d{1,2})$/);
    const legacy = normalized.match(/^(\d{1,2}) 位$/);
    const digits = Number(scientific?.[1] ?? legacy?.[1]);
    if (!Number.isFinite(digits))
        throw new Error(`内部评分精度格式无效：${value}`);
    return Math.max(1, Math.min(MAX_SCORE_PRECISION_DIGITS, digits));
}
function parseFragmentAnalysis(values, sections, wheelKeys, characterByName, sourceByName, baselineObjectiveScore) {
    const statusLabel = values.get('碎片分析状态') ?? '失败';
    const status = statusLabel === '精确完成' ? 'completed' : statusLabel === '估算结果' ? 'estimated' : statusLabel === '已取消' ? 'cancelled' : 'failed';
    const relaxedText = values.get('理想方案评分');
    const improvementText = values.get('评分提升');
    const relaxedObjectiveScore = relaxedText && relaxedText !== '无' ? parseNumber(relaxedText, '理想方案评分') : undefined;
    const objectiveImprovement = improvementText && improvementText !== '无' ? parseNumber(improvementText, '评分提升') : undefined;
    const message = values.get('碎片分析说明');
    const elapsedMilliseconds = Math.round(parseNumber((values.get('碎片分析耗时') ?? '0').replace(' 秒', ''), '碎片分析耗时') * 1000);
    const wheelByName = new Map([...wheelKeys.values()].map((wheel) => [wheel.name, wheel]));
    const shortageLines = sections.get(SECTION_FRAGMENT_SHORTAGES) ?? [];
    const shortages = shortageLines.filter((line) => line !== '无').map((line) => {
        const [name = '', available = '', required = '', shortage = '', contribution = '贡献 0%', affected = '影响 无'] = line.split('｜');
        const character = characterByName.get(name);
        if (!character)
            throw new Error(`当前工作台没有命轮“${name}”，无法读取碎片缺口。`);
        const affectedNames = affected.replace('影响 ', '');
        const shortageValue = parseNumber(shortage.replace('缺少 ', ''), `命轮“${name}”缺少碎片`);
        const isPerFragment = contribution.startsWith('每片贡献 ');
        const isBasisPointPerItem = contribution.includes('‱/个');
        const contributionValue = parseNumber(contribution.replace(/^(每片贡献|贡献) /, '').replace('‱/个', '').replace('%/片', '').replace('%', ''), `命轮“${name}”贡献`);
        const contributionPercent = isPerFragment
            ? Number(((isBasisPointPerItem ? contributionValue / 100 : contributionValue) * shortageValue).toFixed(6))
            : contributionValue;
        return { characterId: character.id, available: parseNumber(available.replace('已有 ', ''), `命轮“${name}”已有碎片`), required: parseNumber(required.replace('需求 ', ''), `命轮“${name}”需求碎片`), shortage: shortageValue, contributionPercent, affectedFateWheelIds: affectedNames === '无' ? [] : affectedNames.split('、').map((wheelName) => wheelByName.get(wheelName)?.id).filter((id) => Boolean(id)) };
    });
    const changeLines = sections.get(SECTION_FRAGMENT_CHANGES) ?? [];
    const wheelChanges = changeLines.filter((line) => line !== '无').map((line) => {
        const [name = '', category = '', members = '', stage = '', contribution = '', gainsText = '收益 无变化'] = line.split('｜');
        const wheel = wheelKeys.get([name, category, members].join('｜'));
        if (!wheel)
            throw new Error(`当前工作台无法匹配碎片分析中的关系命轮“${name}”（${category}／${members}）。`);
        const match = /^(\d+) 阶 (\d+) 星 → (\d+) 阶 (\d+) 星$/.exec(stage);
        if (!match)
            throw new Error(`关系命轮“${name}”的方案变化无法解析。`);
        const contributionValue = contribution.replace('贡献 ', '');
        const isPercent = contributionValue.endsWith('%');
        const contributionPercent = isPercent ? parseNumber(contributionValue.replace('%', ''), `关系命轮“${name}”贡献`) : 0;
        const contributionDelta = isPercent && objectiveImprovement !== undefined ? objectiveImprovement * contributionPercent / 100 : parseNumber(contributionValue, `关系命轮“${name}”贡献变化`);
        const rawGains = gainsText.replace('收益 ', '');
        const gains = rawGains === '无变化' ? [] : rawGains.split('、').map((part) => { const split = part.lastIndexOf(' '); const sourceName = part.slice(0, split); const source = sourceByName.get(sourceName); if (!source)
            throw new Error(`当前工作台没有收益“${sourceName}”。`); const valueText = part.slice(split + 1); return { sourceAttributeId: source.id, valueKind: valueText.endsWith('%') ? 'percent' : 'flat', value: parseNumber(valueText.replace('%', ''), `收益“${sourceName}”`) }; });
        return { fateWheelId: wheel.id, fromRank: Number(match[1]), fromStar: Number(match[2]), toRank: Number(match[3]), toStar: Number(match[4]), contributionDelta, contributionPercent, gains, fragmentChanges: [] };
    });
    return { status, elapsedMilliseconds, isProvenOptimal: status === 'completed', ...(baselineObjectiveScore === undefined ? {} : { baselineObjectiveScore }), ...(relaxedObjectiveScore === undefined ? {} : { relaxedObjectiveScore }), ...(objectiveImprovement === undefined ? {} : { objectiveImprovement }), shortages, wheelChanges, ...(message ? { message } : {}) };
}
function findValue(lines, label) { const prefix = `${label}：`; return lines?.find((line) => line.startsWith(prefix))?.slice(prefix.length); }
function parseNumber(value, label) { const number = Number(value.trim()); if (!Number.isFinite(number))
    throw new Error(`${label}不是有效数字。`); return number; }
function formatNumber(value) { return Number.isInteger(value) ? String(value) : String(Number(value.toFixed(8))); }
function singleLine(value) { return value.replace(/[\r\n]+/g, ' ').trim(); }
function friendlyTime(value) { const date = new Date(value); if (!Number.isFinite(date.getTime()))
    return value; const parts = new Intl.DateTimeFormat('zh-CN', { timeZone: 'Asia/Shanghai', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' }).formatToParts(date); const get = (type) => parts.find((item) => item.type === type)?.value ?? ''; return `${get('year')}-${get('month')}-${get('day')} ${get('hour')}:${get('minute')}:${get('second')}`; }
function parseFriendlyTime(value) { if (!value)
    return new Date().toISOString(); const parsed = new Date(`${value.replace(' ', 'T')}+08:00`); return Number.isFinite(parsed.getTime()) ? parsed.toISOString() : new Date().toISOString(); }
function stableFingerprint(value) {
    let left = 0x811c9dc5;
    let right = 0x9e3779b9;
    for (let index = 0; index < value.length; index += 1) {
        const code = value.charCodeAt(index);
        left = Math.imul(left ^ code, 0x01000193) >>> 0;
        right = Math.imul(right ^ code, 0x85ebca6b) >>> 0;
    }
    return `${left.toString(16).padStart(8, '0')}${right.toString(16).padStart(8, '0')}${((left ^ right) >>> 0).toString(16).padStart(8, '0')}`;
}
//# sourceMappingURL=solver-record-file.js.map